/**
 * Trail Payload Builders
 * Construct API payloads for different trail types
 */

import {
  MantrailingTrailPayload,
  DetectionTrailPayload,
  HikingTrail,
} from "../../types/trail";
import { createGeoJSONFeatureCollection, coordsToGeoJSONPoint } from "../gpx";

interface MantrailingFormData {
  selectedDogId: string;
  handlerName: string;
  trainer: string;
  date: string;
  location: string;
  distance: number;
  duration: number;
  notes: string;
  trailType: string;
  startType: "knowing" | "blind";
  delay: number;
  trainerComment: string;
  dogGpxData: any;
  userGpxData: any;
  locationCoordinate: [number, number];
}

interface DetectionFormData {
  selectedDogId: string;
  duration: number;
  difficulty: "Easy" | "Moderate" | "Hard" | "Expert";
  targetScent: string;
  notes: string;
  date: string;
  location: string;
  trainer: string;
  trainerComment: string;
}

interface HikingFormData {
  name: string;
  description: string;
  date: string;
  distance: number;
  duration: number;
  elevationGain: number;
  difficulty: "Easy" | "Moderate" | "Hard" | "Expert";
  userGpxData: any;
  dogGpxData: any;
  locationCoordinate: [number, number];
  existingPhotos: string[];
}

/**
 * Build mantrailing trail payload for API submission
 * @param formData Mantrailing form data
 * @returns MantrailingTrailPayload ready for API
 */
export const buildMantrailingPayload = (
  formData: MantrailingFormData,
): MantrailingTrailPayload => {
  return {
    category: "mantrailing",
    dog: formData.selectedDogId,
    handlerName: formData.handlerName,
    trainer: formData.trainer,
    date: formData.date,
    location: formData.location,
    distance: formData.distance,
    duration: formData.duration,
    notes: formData.notes,
    trailType: formData.trailType,
    startType: formData.startType,
    delay: formData.delay,
    locationCoordinate:
      formData.dogGpxData?.startPoint || formData.locationCoordinate,
    dogTrace: createGeoJSONFeatureCollection(formData.dogGpxData),
    runnerTrace: createGeoJSONFeatureCollection(formData.userGpxData),
    trainerComment: formData.trainerComment,
  };
};

/**
 * Build detection trail payload for API submission
 * @param formData Detection form data
 * @returns DetectionTrailPayload ready for API
 */
export const buildDetectionPayload = (
  formData: DetectionFormData,
): DetectionTrailPayload => {
  return {
    category: "detection",
    dog: formData.selectedDogId,
    duration: formData.duration,
    difficulty: formData.difficulty,
    targetScent: formData.targetScent,
    notes: formData.notes,
    date: formData.date,
    location: formData.location,
    trainer: formData.trainer,
    trainerComment: formData.trainerComment,
  };
};

/**
 * Build hiking trail payload for API submission
 * @param formData Hiking form data
 * @returns Partial HikingTrail ready for API
 */
export const buildHikingPayload = (
  formData: HikingFormData,
): Partial<HikingTrail> => {
  return {
    category: "hiking",
    name: formData.name,
    description: formData.description,
    date: formData.date,
    distance: formData.distance,
    duration: formData.duration,
    elevationGain: formData.elevationGain,
    difficulty: formData.difficulty,
    startLocation:
      formData.userGpxData?.startPoint || formData.locationCoordinate
        ? coordsToGeoJSONPoint(
            formData.userGpxData?.startPoint || formData.locationCoordinate,
          )
        : undefined,
    userTrack: createGeoJSONFeatureCollection(formData.userGpxData),
    dogTrack: createGeoJSONFeatureCollection(formData.dogGpxData),
    photos: formData.existingPhotos,
  };
};
